import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { methodologyService } from "../../lib/firebase";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ADMIN_EMAIL = "admin@rawpa.com";

interface UserStatistics {
  totalUsers: number;
  activeUsers: number;
  averageSessionsPerUser: number;
  usersByDate: Record<string, number>;
}

interface MethodologyUsage {
  straight: { id: string; title: string; count: number }[];
  guided: { id: string; title: string; count: number }[];
}

interface UserSession {
  userId: string;
  email?: string;
  displayName?: string;
  lastActive: string;
  methodologies: {
    type: 'straight-up' | 'guided';
    id: string;
    title: string;
    lastAccessed: string;
  }[];
}

export const AdminUserManagement = (): JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState<UserStatistics | null>(null);
  const [methodologyUsage, setMethodologyUsage] = useState<MethodologyUsage | null>(null);
  const [userSessions, setUserSessions] = useState<UserSession[]>([]);
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || user.email !== ADMIN_EMAIL) {
        navigate('/aAyuiOcvMq');
      }
    });

    // Fetch data
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch user statistics
        const stats = await methodologyService.getUserStatistics();
        setStatistics(stats);
        
        // Fetch methodology usage
        const usage = await methodologyService.getMostUsedMethodologies();
        setMethodologyUsage(usage);
        
        // Fetch user sessions
        const sessions = await methodologyService.getAllUserSessions();
        setUserSessions(sessions);
      } catch (error) {
        console.error('Error fetching user management data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();

    return () => unsubscribe();
  }, [navigate]);

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/aAyuiOcvMq');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Prepare chart data for user activity
  const getUserActivityChartData = () => {
    if (!statistics) return null;
    
    // Sort dates in ascending order
    const sortedDates = Object.keys(statistics.usersByDate).sort();
    
    return {
      labels: sortedDates.map(date => {
        const d = new Date(date);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }),
      datasets: [
        {
          label: 'Active Users',
          data: sortedDates.map(date => statistics.usersByDate[date]),
          backgroundColor: 'rgba(218, 237, 110, 0.7)',
          borderColor: 'rgba(197, 216, 92, 1)',
          borderWidth: 1,
        },
      ],
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA Admin</span>
            </div>
            <div 
              onClick={handleSignOut}
              className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
            >
              <img className="w-6 h-6" alt="Sign Out" src="/user-icon.svg" />
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-12">
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          <div className="mb-8 flex justify-between items-center">
            <div>
              <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
                User Management
              </h1>
              <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
                Monitor user activity and methodology usage
              </p>
            </div>
            <Button 
              className="h-10 px-4 bg-gray-100 text-black rounded-lg hover:bg-gray-200 transition-colors"
              onClick={() => navigate('/aAyuiOcvMq/dashboard')}
            >
              Back to Dashboard
            </Button>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm">
              <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-2">
                Total Users
              </h3>
              <div className="text-3xl font-bold">{statistics?.totalUsers || 0}</div>
              <p className="text-sm text-black opacity-80 mt-1">Registered users</p>
            </div>

            <div className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm">
              <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-2">
                Active Users
              </h3>
              <div className="text-3xl font-bold">{statistics?.activeUsers || 0}</div>
              <p className="text-sm text-black opacity-80 mt-1">Last 24 hours</p>
            </div>

            <div className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm">
              <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-2">
                Avg. Sessions
              </h3>
              <div className="text-3xl font-bold">
                {statistics?.averageSessionsPerUser.toFixed(1) || '0.0'}
              </div>
              <p className="text-sm text-black opacity-80 mt-1">Per user</p>
            </div>

            <div className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm">
              <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-2">
                Activity Rate
              </h3>
              <div className="text-3xl font-bold">
                {statistics && statistics.totalUsers > 0
                  ? Math.round((statistics.activeUsers / statistics.totalUsers) * 100)
                  : 0}%
              </div>
              <p className="text-sm text-black opacity-80 mt-1">Active / Total users</p>
            </div>
          </div>

          {/* User Activity Chart */}
          <div className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm mb-8">
            <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-4">
              User Activity (Last 7 Days)
            </h3>
            <div className="h-64">
              {statistics && getUserActivityChartData() && (
                <Bar
                  data={getUserActivityChartData()!}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          precision: 0
                        }
                      }
                    }
                  }}
                />
              )}
            </div>
          </div>

          {/* Most Used Methodologies */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Straight-up Methodologies */}
            <div className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm">
              <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-4">
                Most Used Straight-up Methodologies
              </h3>
              {methodologyUsage && methodologyUsage.straight.length > 0 ? (
                <div className="space-y-3">
                  {methodologyUsage.straight.slice(0, 5).map((item, index) => (
                    <div key={item.id} className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-[#daed6e] flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </span>
                        <span className="font-medium truncate max-w-[200px]">{item.title}</span>
                      </div>
                      <span className="bg-gray-100 px-2 py-1 rounded text-sm font-medium">
                        {item.count} {item.count === 1 ? 'user' : 'users'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic">No data available</p>
              )}
            </div>

            {/* Guided Methodologies */}
            <div className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm">
              <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-4">
                Most Used Guided Methodologies
              </h3>
              {methodologyUsage && methodologyUsage.guided.length > 0 ? (
                <div className="space-y-3">
                  {methodologyUsage.guided.slice(0, 5).map((item, index) => (
                    <div key={item.id} className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-[#daed6e] flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </span>
                        <span className="font-medium truncate max-w-[200px]">{item.title}</span>
                      </div>
                      <span className="bg-gray-100 px-2 py-1 rounded text-sm font-medium">
                        {item.count} {item.count === 1 ? 'user' : 'users'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic">No data available</p>
              )}
            </div>
          </div>

          {/* User Sessions Table */}
          <div className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm">
            <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black mb-4">
              User Sessions
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px] border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-sm">User ID</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm">User</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm">Last Active</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm">Methodologies</th>
                  </tr>
                </thead>
                <tbody>
                  {userSessions.length > 0 ? (
                    userSessions.map((session) => (
                      <tr key={session.userId} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm">
                          <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                            {session.userId.substring(0, 8)}...
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {session.email || session.displayName || 'Anonymous User'}
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {formatDate(session.lastActive)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-2">
                            {session.methodologies.map((m, i) => (
                              <span
                                key={i}
                                className={`text-xs px-2 py-1 rounded ${
                                  m.type === 'guided'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-green-100 text-green-800'
                                }`}
                              >
                                {m.title}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="py-4 text-center text-gray-500">
                        No user sessions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 